import { Field, Chip, Avatar } from '../ds/index.js'
import { dateToInput, dateInputToDate, optionColor } from '../lib/format.js'

/**
 * Generic typed property field. Renders a label + an editor chosen by def.type.
 * Text-like fields commit on blur; selects/checkbox/user commit on change.
 */
export default function PropertyField({ def, value, onChange, users = [] }) {
  switch (def.type) {
    case 'textarea':
      return (
        <div style={{ marginBottom: 12 }}>
          <Field label={def.label} as="textarea" rows={3}
            defaultValue={value ?? ''} onBlur={(e) => onChange(e.target.value)} />
        </div>
      )

    case 'select':
      return (
        <div style={{ marginBottom: 12 }}>
          <Field label={def.label} as="select" value={value ?? ''}
            onChange={(e) => onChange(e.target.value || null)}>
            <option value="">—</option>
            {(def.options || []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Field>
        </div>
      )

    case 'multiselect': {
      const vals = Array.isArray(value) ? value : []
      return (
        <div style={{ marginBottom: 12 }}>
          <span className="cn-field-label">{def.label}</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {(def.options || []).map((o) => {
              const on = vals.includes(o.value)
              return (
                <Chip key={o.value} as="button" type="button" color={optionColor(def.options, o.value)}
                  onClick={() => onChange(on ? vals.filter((v) => v !== o.value) : [...vals, o.value])}
                  style={{ opacity: on ? 1 : 0.4, cursor: 'pointer' }}>
                  {o.label}
                </Chip>
              )
            })}
          </div>
        </div>
      )
    }

    case 'date':
      return (
        <div style={{ marginBottom: 12 }}>
          <Field label={def.label} type="date"
            defaultValue={dateToInput(value)} onBlur={(e) => onChange(dateInputToDate(e.target.value))} />
        </div>
      )

    case 'number':
    case 'currency':
      return (
        <div style={{ marginBottom: 12 }}>
          <Field label={def.label} type="number"
            defaultValue={value ?? ''} onBlur={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
        </div>
      )

    case 'checkbox':
      return (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
          <span className="cn-field-label">{def.label}</span>
        </div>
      )

    case 'user':
      return (
        <div style={{ marginBottom: 12 }}>
          <span className="cn-field-label">{def.label}</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <Avatar user={{ color: 'transparent', ink: 'var(--color-muted)', initials: '–' }} size={26}
              onClick={() => onChange(null)}
              style={{ border: '1px solid var(--color-divider-dark)', cursor: 'pointer' }} />
            {users.map((u) => (
              <Avatar key={u.uid} user={u} size={26} title={u.name} onClick={() => onChange(u.uid)}
                style={{ outline: value === u.uid ? '2px solid var(--color-gold)' : 'none', cursor: 'pointer' }} />
            ))}
          </div>
        </div>
      )

    case 'url':
    case 'text':
    default:
      return (
        <div style={{ marginBottom: 12 }}>
          <Field label={def.label} type={def.type === 'url' ? 'url' : 'text'}
            defaultValue={value ?? ''} onBlur={(e) => onChange(e.target.value)} />
        </div>
      )
  }
}
